import React from 'react';
import { Box } from '@mui/material';
import { EmailAnalytics } from '../../types';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ReactLoading from 'react-loading';
import DoneIcon from '@mui/icons-material/Done';
import DraftsIcon from '@mui/icons-material/Drafts';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import CloseIcon from '@mui/icons-material/Close';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';

const StatusStatistics = ({
  isLoading,
  emailAnalytics
}: {
  isLoading: boolean;
  emailAnalytics: EmailAnalytics;
}) => {
  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  const stats = emailAnalytics?.statistics?.percentages;

  if (!stats) {
    return <Box>No statistics available</Box>;
  }

  return (
    <Box
      sx={{
        border: '1px solid #E3E3E3',
        borderRadius: '3px',
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        paddingLeft: '4%',
        height: '100%',
        marginTop: '50px',
        paddingRight: '4%'
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            height: '300px',
            margin: 'auto',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex'
          }}
        >
          <ReactLoading type="spin" color="#084D6D" width={100} height={100} />
        </Box>
      ) : (
        <Box sx={{ width: '100%', display: 'flex' }}>
          {/* Left side - Statistics Cards */}
          <Box sx={{ flex: '0 0 50%', paddingRight: '25px' }}>
            {/* Container for all cards with proper spacing */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '16px',
                padding: '40px 0 15px 0',
                width: '24rem',
                margin: '0 auto'
              }}
            >
              {/* Top row - Delivered (full width) */}
              <Box
                sx={{
                  margin: '0.5rem',
                  padding: '1.5rem',
                  backgroundColor: stats.delivered
                    ? 'rgba(109, 190, 191, 0.2)'
                    : 'rgba(229, 229, 229, 0.5)'
                }}
              >
                <Box
                  sx={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: stats.delivered ? '#6DBEBF' : '#666666'
                  }}
                >
                  <Box sx={{ width: '5.5rem', margin: 'auto' }}>
                    <DoneIcon
                      sx={{
                        color: stats.delivered ? '#6DBEBF' : '#666666',
                        fontSize: '23px'
                      }}
                    />
                    <Box>{stats.delivered_pcent || 0}%</Box>
                    <Box sx={{ fontSize: '15px', fontWeight: '400', display: 'flex' }}>
                      {stats.delivered || 0}
                      <Box sx={{ color: 'black', marginLeft: '3px' }}> Delivered</Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Bottom row - 2x2 grid of other stats */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px'
                }}
              >
                {/* Opened */}
                <Box
                  sx={{
                    margin: '0.5rem',
                    padding: '1.5rem',
                    backgroundColor: stats.opened
                      ? 'rgba(146, 104, 247, 0.2)'
                      : 'rgba(229, 229, 229, 0.5)'
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: stats.opened ? '#9268F7' : '#666666'
                    }}
                  >
                    <Box sx={{ width: '5.5rem', margin: 'auto' }}>
                      <DraftsIcon
                        sx={{
                          color: stats.opened ? '#9268F7' : '#666666',
                          fontSize: '23px'
                        }}
                      />
                      <Box>{stats.opened_pcent || 0}%</Box>
                      <Box sx={{ fontSize: '15px', fontWeight: '400', display: 'flex' }}>
                        {stats.opened || 0}
                        <Box sx={{ color: 'black', marginLeft: '3px' }}> Opened</Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Clicks */}
                <Box
                  sx={{
                    margin: '0.5rem',
                    padding: '1.5rem',
                    backgroundColor: stats.clicks
                      ? 'rgba(247, 161, 255, 0.2)'
                      : 'rgba(229, 229, 229, 0.5)'
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: stats.clicks ? '#F7A1FF' : '#666666'
                    }}
                  >
                    <Box sx={{ width: '5.5rem', margin: 'auto' }}>
                      <AdsClickIcon
                        sx={{
                          color: stats.clicks ? '#F7A1FF' : '#666666',
                          fontSize: '23px'
                        }}
                      />
                      <Box>{stats.clicks_pcent || 0}%</Box>
                      <Box sx={{ fontSize: '15px', fontWeight: '400', display: 'flex' }}>
                        {stats.clicks || 0}
                        <Box sx={{ color: 'black', marginLeft: '3px' }}> Clicks</Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Bounced */}
                <Box
                  sx={{
                    margin: '0.5rem',
                    padding: '1.5rem',
                    backgroundColor: stats.bounced
                      ? 'rgba(236, 110, 133, 0.2)'
                      : 'rgba(229, 229, 229, 0.5)'
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: stats.bounced ? '#EC6E85' : '#666666'
                    }}
                  >
                    <Box sx={{ width: '5.5rem', margin: 'auto' }}>
                      <CloseIcon
                        sx={{
                          color: stats.bounced ? '#EC6E85' : '#666666',
                          fontSize: '23px'
                        }}
                      />
                      <Box>{stats.bounced_pcent || 0}%</Box>
                      <Box sx={{ fontSize: '15px', fontWeight: '400', display: 'flex' }}>
                        {stats.bounced || 0}
                        <Box sx={{ color: 'black', marginLeft: '3px' }}> Bounced</Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Unsubscribed */}
                <Box
                  sx={{
                    margin: '0.5rem',
                    padding: '1.5rem',
                    backgroundColor: stats.unsubscribed
                      ? 'rgba(126, 167, 228, 0.2)'
                      : 'rgba(229, 229, 229, 0.5)'
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: stats.unsubscribed ? '#7EA7E4' : '#666666'
                    }}
                  >
                    <Box sx={{ width: '5.5rem', margin: 'auto' }}>
                      <UnsubscribeIcon
                        sx={{
                          color: stats.unsubscribed ? '#7EA7E4' : '#666666',
                          fontSize: '23px'
                        }}
                      />
                      <Box>{stats.unsubscribed_pcent || 0}%</Box>
                      <Box sx={{ fontSize: '15px', fontWeight: '400', display: 'flex' }}>
                        {stats.unsubscribed || 0}
                        <Box sx={{ color: 'black', marginLeft: '3px' }}> Unsubscribed</Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right side - Bar Chart */}
          <Box sx={{ flex: '0 0 50%', paddingLeft: '25px', margin: 'auto' }}>
            <Box sx={{ position: 'relative', minHeight: '300px', maxHeight: '500px' }}>
              {(() => {
                const statusStats = emailAnalytics?.statistics?.status_statistics;
                if (!statusStats || !statusStats.labels || !statusStats.datasets) {
                  return <Box>No chart data available</Box>;
                }

                // Only use the first dataset (Status Statistics) for the bar chart
                const firstDataset = statusStats.datasets[0];
                if (!firstDataset || !firstDataset.data) {
                  return <Box>No dataset data available</Box>;
                }

                const chartData = {
                  labels: statusStats.labels,
                  datasets: [
                    {
                      label: firstDataset.label || 'Status Statistics',
                      data: firstDataset.data,
                      backgroundColor: firstDataset.backgroundColor || [
                        'rgba(140, 207, 149, 1)', // Sent - Green
                        'rgba(109, 190, 191, 1)', // Delivered - Teal
                        'rgba(146, 104, 247, 1)', // Opened - Purple
                        'rgba(247, 161, 255, 1)', // Clicks - Pink
                        'rgba(236, 110, 133, 1)', // Bounced - Red
                        'rgba(126, 167, 228, 1)' // Unsubscribed - Blue
                      ]
                    }
                  ]
                };

                return (
                  <Bar
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1
                          }
                        }
                      },
                      plugins: {
                        legend: {
                          display: false
                        }
                      }
                    }}
                    data={chartData}
                  />
                );
              })()}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StatusStatistics;
